import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { VL_BLACK, WHITE } from '../../utils/colours';
import useKeyboard from '../../hooks/useKeyboard';

// Styled Tab Container
const Bar = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-around;
    background: ${({ $backgroundColour }) => $backgroundColour || VL_BLACK()};
    z-index: 100;
    padding: 5px 0;

    ${({ $keyboardVisible }) => $keyboardVisible ? `
        position: fixed;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        bottom: -999px;
    ` : ''}

`;

const IconContainer = styled.div.attrs({ 'aria-hidden': true })`
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
        width: auto;
        height: auto;
        flex-grow: 1;
        max-width: 20px;
        max-height: 20px;
    }
`;

IconContainer.displayName = 'IconContainer';

// Styled NavLink
const Tab = styled(NavLink).attrs(({ $menuOpen }) => $menuOpen ? {} : { activeClassName: 'selected' })`
    text-decoration: none;
    font-size: 12px;
    font-weight: 100;
    color: ${({ $textColour }) => $textColour || WHITE()};
    background: none;
    border: none;
    padding: 10px 6px;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    text-align: center;

    &.selected {
        background: ${({ $accentColour }) => $accentColour || WHITE(25)};
        color: ${({ $alternativeTextColour }) => $alternativeTextColour || VL_BLACK()};
    }
`;

// Styled button for 'More'
const MoreTab = styled.button`
    font-size: 12px;
    font-weight: 100;
    color: ${({ $textColour }) => $textColour || WHITE()};
    background: none;
    border: none;
    padding: 10px 6px;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    text-align: center;
    cursor: pointer;

    ${({ $isSelected, $accentColour, $alternativeTextColour }) => $isSelected && `
        background: ${$accentColour || WHITE(25)};
        color: ${$alternativeTextColour || VL_BLACK()};
    `}
`;

const BottomTabBar = ({
    tabs,
    onMoreClick,
    menuOpen,
    currentPath,
    accentColour,
    textColour,
    backgroundColour,
    alternativeTextColour
}) => {

    const { isKeyboardVisible } = useKeyboard();
    const { icon: MoreIcon, label } = _.last(tabs);

    return (
        <Bar 
            $backgroundColour={backgroundColour}
            $keyboardVisible={isKeyboardVisible}
        >
            {tabs.slice(0, -1).map(({ path, icon: Icon, label }) => (
                <Tab
                    to={path}
                    key={path}
                    $menuOpen={menuOpen}
                    $accentColour={accentColour}
                    $textColour={textColour}
                    $alternativeTextColour={alternativeTextColour}
                >
                    <IconContainer>
                        <Icon />
                    </IconContainer>
                    {label}
                </Tab>
            ))}
            <MoreTab
                onClick={onMoreClick}
                $accentColour={accentColour}
                $textColour={textColour}
                $alternativeTextColour={alternativeTextColour}
                $isSelected={menuOpen}
            >
                {
                    MoreIcon && (
                        <IconContainer>
                            <MoreIcon />
                        </IconContainer>
                    )
                }
                {label}
            </MoreTab>
        </Bar>
    );
};

BottomTabBar.propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.shape({
        path: PropTypes.string,
        icon: PropTypes.elementType.isRequired,
        label: PropTypes.string.isRequired
    })).isRequired,
    /** Is the mobile menu currently open? */
    menuOpen: PropTypes.bool,
    onMoreClick: PropTypes.func.isRequired,
    currentPath: PropTypes.string.isRequired,
    accentColour: PropTypes.string,
    textColour: PropTypes.string,
    backgroundColour: PropTypes.string,
    alternativeTextColour: PropTypes.string
};

export default BottomTabBar;